import { Link } from 'react-router-dom';
import GlobeAnimation from '../components/GlobeAnimation';
import HowItWorksDiagram from '../components/HowItWorksDiagram';
import SiteHeader from '../components/SiteHeader';

const FEATURES = [
  {
    title: 'Powered by QGIS Processing',
    text: 'GIS Liberty uses your installed QGIS as the geospatial processor. Every algorithm and Model Designer workflow runs through real QGIS Processing.',
  },
  {
    title: 'Custom models in the browser',
    text: 'Publish QGIS models to a web toolbox — guided parameters, recipes, jobs, schedules, maps, and analytics for operators who never open desktop QGIS.',
  },
  {
    title: 'Deployed where you need it',
    text: 'Run GIS Liberty in your organization. Your data stays with you — this marketing site never uploads or processes your files.',
  },
];

function IconPin() {
  return (
    <svg className="contact-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
      />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg className="contact-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.62 10.79a15.15 15.15 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.25 1.02l-2.2 2.19z"
      />
    </svg>
  );
}

function IconMail() {
  return (
    <svg className="contact-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"
      />
    </svg>
  );
}

function IconWeb() {
  return (
    <svg className="contact-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.93 6h-3.16a15.4 15.4 0 0 0-1.35-3.42A8.03 8.03 0 0 1 18.93 8zM12 4c.7 0 1.86 1.55 2.5 4h-5c.64-2.45 1.8-4 2.5-4zM4.26 14a7.98 7.98 0 0 1 0-4h3.48a17.3 17.3 0 0 0-.1 2c0 .68.03 1.35.1 2H4.26zm.81 2h3.16c.3 1.25.75 2.45 1.35 3.42A8.03 8.03 0 0 1 5.07 16zM8.5 8H5.07a8.03 8.03 0 0 1 4.51-3.42C8.98 5.55 8.8 6.75 8.5 8zM12 20c-.7 0-1.86-1.55-2.5-4h5c-.64 2.45-1.8 4-2.5 4zm2.6-6H9.4a15.4 15.4 0 0 1-.1-2c0-.68.03-1.35.1-2h5.2c.07.65.1 1.32.1 2s-.03 1.35-.1 2zm.73 5.42c.6-.97 1.05-2.17 1.35-3.42h3.16a8.03 8.03 0 0 1-4.51 3.42zM16.52 14c.07-.65.1-1.32.1-2s-.03-1.35-.1-2h3.48a7.98 7.98 0 0 1 0 4h-3.48z"
      />
    </svg>
  );
}

export default function LandingPage() {
  return (
    <div className="site-page">
      <SiteHeader />

      <section className="hero">
        <div className="hero-globe">
          <GlobeAnimation size={280} className="hero-globe-svg" />
          <p className="hero-company">GIS Innovation Sdn Bhd</p>
          <div className="hero-qgis-badge">
            <img src="/qgis-logo.svg" alt="QGIS" width="36" height="36" />
            <span>Powered by QGIS</span>
          </div>
        </div>
        <div className="hero-copy">
          <p className="hero-eyebrow">Geospatial processing platform</p>
          <h1>Freedom to process geospatial data anywhere</h1>
          <p className="hero-lead">
            GIS Liberty is a web interface for <strong>QGIS Processing</strong>.
            Your QGIS installation is the engine — GIS Liberty orchestrates the toolbox,
            jobs, schedules, results, and reports in the browser.
          </p>
          <div className="hero-actions">
            <Link to="/login" className="btn btn-primary">Try the interactive demo</Link>
            <a href="mailto:sales@gis.fm?subject=GIS%20Liberty%20demo%20request" className="btn btn-outline">
              Request a demo
            </a>
          </div>
          <p className="hero-note">
            Demo sign-in: <code>administrator</code> / <code>administrator</code>.
            Browse files shows filenames only — nothing is uploaded.
          </p>
        </div>
      </section>

      <section id="features" className="features">
        <h2>What GIS Liberty does</h2>
        <div className="features-grid">
          {FEATURES.map((feature) => (
            <article key={feature.title} className="feature-card panel-card">
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="how-it-works" className="how-section">
        <div className="how-section-head">
          <h2>How GIS Liberty works</h2>
          <p>
            From browser click to QGIS Processing and back to map, analytics, and downloads —
            hover or tap a step to pause the walkthrough.
          </p>
        </div>
        <div className="how-section-card panel-card">
          <HowItWorksDiagram />
        </div>
      </section>

      <section className="qgis-band panel-card">
        <img src="/qgis-logo.svg" alt="" className="qgis-band-logo" width="56" height="56" />
        <div>
          <h2>QGIS is the processor</h2>
          <p>
            Algorithms, GDAL tools, and custom Model Designer models are executed by
            <strong> QGIS Processing</strong>.
            GIS Liberty provides the web UI, jobs, recipes, schedules, dashboards, and PDF reports —
            QGIS does the geospatial work you already trust.
          </p>
        </div>
      </section>

      <section className="cta-band panel-card">
        <div>
          <h2>Explore the interface</h2>
          <p>
            Browse custom models, load a fake recipe, preview jobs, dashboard, and schedules —
            light and dark mode included.
          </p>
        </div>
        <Link to="/login" className="btn btn-primary">Enter demo</Link>
      </section>

      <section id="contact" className="contact-band panel-card">
        <h2>Contact</h2>
        <p className="contact-email-line">
          Please email us at{' '}
          <a href="mailto:sales@gis.fm">sales@gis.fm</a>
          {' '}to request a demo or learn more about GIS Liberty.
        </p>

        <div className="contact-company">
          <strong>GIS Innovation Sdn Bhd</strong>
          <span className="contact-company-alt">/ GIS Innovation Pte Ltd</span>
        </div>

        <ul className="contact-details">
          <li>
            <IconPin />
            <div>
              <span className="contact-label">Address</span>
              <address>
                Unit 819, 8th floor, Block A, Damansara Intan,<br />
                No. 1 Jalan SS 20/27,<br />
                47400 Petaling Jaya,<br />
                Selangor Darul Ehsan, Malaysia
              </address>
            </div>
          </li>
          <li>
            <IconPhone />
            <div>
              <span className="contact-label">Tel</span>
              <a href="tel:+60377325016">+603 – 7732 5016</a>
            </div>
          </li>
          <li>
            <IconMail />
            <div>
              <span className="contact-label">Email</span>
              <a href="mailto:sales@gis.fm">sales@gis.fm</a>
            </div>
          </li>
          <li>
            <IconWeb />
            <div>
              <span className="contact-label">Web</span>
              <a href="https://www.gis.com.my" target="_blank" rel="noopener noreferrer">
                www.GIS.com.my
              </a>
            </div>
          </li>
        </ul>
      </section>

      <footer className="site-footer">
        <p>
          © GIS Innovation Sdn Bhd · Powered by QGIS
        </p>
      </footer>
    </div>
  );
}
